import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { API, inr, errMsg } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Cart() {
  const { cart, setCart } = useCart();
  const nav = useNavigate();
  const upd = async (it, qty) => { try { const { data } = await API.put(`/cart/${it.id}`, { qty, variant_id: it.variant_id || null }); setCart(data); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (it) => { try { const { data } = await API.delete(`/cart/${it.id}${it.variant_id ? `?variant_id=${it.variant_id}` : ""}`); setCart(data); } catch (e) { toast.error(errMsg(e)); } };

  if (!cart || cart.count === 0)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-14 h-14 text-stone-300 mx-auto" />
        <h1 className="font-display text-3xl font-semibold mt-6">Your Bag Is Empty</h1>
        <p className="text-stone-500 mt-2">Discover jewellery, cosmetics and more.</p>
        <Link to="/products" className="inline-block mt-6 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold px-6 py-3 rounded-full" data-testid="cart-shop-link">Shop Now</Link>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-page">
      <h1 className="font-display text-3xl sm:text-4xl font-semibold">Your Bag</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((it) => (
            <div key={`${it.id}-${it.variant_id || "base"}`} className="flex gap-4 bg-white border border-[#EADBD8] rounded-2xl p-3 shadow-soft" data-testid={`cart-item-${it.sku}`}>
              <img src={it.image} alt="" className="w-24 h-28 object-cover rounded-lg" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-[#6A1B29] font-semibold">{it.category}</p>
                <Link to={`/product/${it.id}`} className="font-medium hover:text-[#6A1B29] line-clamp-1">{it.title}</Link>
                {it.variant_name && (
                  <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-1.5" data-testid={`cart-shade-${it.sku}`}>
                    {it.color_code && <span className="w-3 h-3 rounded-full border border-stone-300 inline-block" style={{ background: it.color_code }} />}
                    Shade: <span className="font-medium text-stone-700">{it.variant_name}</span>
                  </p>
                )}
                <p className="text-[11px] text-stone-400">SKU: {it.sku}</p>
                <p className="text-sm mt-1 font-semibold">{inr(it.selling_price)} {it.mrp > it.selling_price && <span className="text-xs text-stone-400 line-through ml-1">{inr(it.mrp)}</span>}</p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-stone-300 rounded-full">
                    <button onClick={() => upd(it, it.qty - 1)} data-testid={`cart-dec-${it.sku}`} className="p-2"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-8 text-center text-sm" data-testid={`cart-qty-${it.sku}`}>{it.qty}</span>
                    <button onClick={() => upd(it, it.qty + 1)} data-testid={`cart-inc-${it.sku}`} className="p-2"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => del(it)} data-testid={`cart-remove-${it.sku}`} className="text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="font-semibold whitespace-nowrap" data-testid={`cart-line-${it.sku}`}>{inr(it.line_total)}</p>
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#EADBD8] rounded-2xl p-6 h-fit shadow-soft" data-testid="cart-summary">
          <h2 className="font-display text-xl font-semibold mb-4">Order Summary</h2>
          <div className="flex justify-between text-sm py-1"><span>Subtotal</span><span>{inr(cart.subtotal)}</span></div>
          {cart.discount > 0 && (
            <div className="flex justify-between text-sm py-1 text-green-700" data-testid="cart-bundle-discount">
              <span>Bundle offer ({cart.bundle.applied}× Buy {cart.bundle.min_qty} @ {inr(cart.bundle.price)})</span>
              <span>-{inr(cart.discount)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-lg border-t border-stone-200 mt-3 pt-3"><span>Total</span><span data-testid="cart-total">{inr(cart.total)}</span></div>
          <button onClick={() => nav("/checkout")} className="w-full mt-5 bg-[#6A1B29] hover:bg-[#501325] text-white font-semibold py-3.5 rounded-full" data-testid="checkout-button">PROCEED TO CHECKOUT</button>
          <p className="text-[11px] text-stone-500 mt-3 text-center">Cash on Delivery available. Free replacement on damaged items.</p>
        </div>
      </div>
    </div>
  );
}
